import { useState } from "react";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ProjectAvailabilityTable } from "@/components/ProjectAvailabilityTable";
import { useProjectAvailability, useSetProjectAvailability } from "@/hooks/useProjectAvailability";

const ProjectAvailability = () => {
  const { data, isLoading, error } = useProjectAvailability();
  const setAvailability = useSetProjectAvailability();
  const [pending, setPending] = useState<Set<string>>(new Set());

  const handleChange = (projectId: string, appId: string, available: boolean) => {
    const key = `${projectId}:${appId}`;
    setPending((prev) => new Set(prev).add(key));
    setAvailability.mutate(
      { projectId, appId, available },
      {
        onSuccess: () => {
          const project = data?.projects.find((p) => p.id === projectId)?.name ?? "Project";
          const app = data?.apps.find((a) => a.id === appId)?.name ?? appId;
          toast.success(available ? `${project} is now available in ${app}` : `${project} removed from ${app}`);
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : "Could not update availability"),
        onSettled: () =>
          setPending((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          }),
      }
    );
  };

  if (error) {
    const notEnabled = error instanceof Error && /not found|404/i.test(error.message);
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {notEnabled
            ? "Project availability needs the project service (PROJECT_SERVICE_ENABLED) to be turned on."
            : `Could not load project availability: ${error instanceof Error ? error.message : "unknown error"}`}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          {data?.is_platform_admin
            ? "You are a platform admin: showing projects from every app. You can make any project available in any registered app."
            : "A project is only available in other apps when its owners share it. You can change projects you own; a platform admin can change any project."}
        </AlertDescription>
      </Alert>
      <ProjectAvailabilityTable
        apps={data?.apps ?? []}
        projects={data?.projects ?? []}
        currentAppId={data?.current_app_id}
        isLoading={isLoading}
        pending={pending}
        onChange={handleChange}
      />
    </div>
  );
};

export default ProjectAvailability;
